import { useState, useEffect } from 'react';
import { AppLayout } from '../../../components/layout';
import { Button, Input, Card, Alert, EmptyState } from '../../../components/core';
import { http, type MeResponse } from '../../../lib/api';
import { useUiStore } from '../../../store/ui';
import type { TenantDto, UserDto } from '@livestreamx/shared-types';

const NAV = [
  { active: 'dashboard', items: [{ to: '/dashboard', label: 'Dashboard', icon: '▦' }] },
  { active: 'streams', items: [{ to: '/streams', label: 'Streams', icon: '▶' }, { to: '/streams/test1/destinations', label: 'Destinations', icon: '🎯' }, { to: '/recordings', label: 'Recordings', icon: '⏺' }, { to: '/analytics', label: 'Analytics', icon: '📊' }] },
  {
    active: 'settings',
    section: 'Settings',
    items: [
      { to: '/settings/profile', label: 'Profile', icon: '👤' },
      { to: '/settings/security', label: 'Security', icon: '🔒' },
      { to: '/settings/preferences', label: 'Preferences', icon: '⚙' },
    ],
  },
  { active: 'settings', items: [{ to: '/billing', label: 'Billing', icon: '💳' }, { to: '/transactions', label: 'Transactions', icon: '🧾' }] },
];

export function ProfilePage() {
  const ui = useUiStore();
  const [tenant, setTenant] = useState<TenantDto | null>(null);
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void http.get<MeResponse>('/api/auth/me').then((data) => {
      setUser(data.user);
      setTenant(data.tenant);
    }).catch((e) => setError(e.message ?? 'Failed to load profile')).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!tenant || !user) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await http.patch('/api/tenant/settings', { name: tenant.name });
      setSuccess('Profile updated');
      ui.toast('success', 'Profile updated');
    } catch (e) {
      setError((e as Error).message ?? 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <EmptyState title="Loading…" />
    </AppLayout>
  );

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="settings-page">
        <h1 className="page-title">Profile</h1>
        <p className="text-muted mb-4">Manage your studio information and user preferences.</p>
        {error && <Alert kind="danger">{error}</Alert>}
        {success && <Alert kind="success">{success}</Alert>}
        <Card title="Studio">
          <Input
            label="Studio name"
            value={tenant?.name ?? ''}
            onChange={(v) => setTenant((t) => (t ? { ...t, name: v } : null))}
            hint="Your company or studio name"
          />
        </Card>
        <Card title="User">
          <div className="text-sm"><span className="text-muted">Email: </span>{user?.email ?? ''}</div>
        </Card>
        <div className="mt-4 flex gap-2">
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
        </div>
      </div>
    </AppLayout>
  );
}
